import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Particles from 'react-tsparticles';
import { Link, Events, scrollSpy } from 'react-scroll';
import { FaGithub, FaLinkedin, FaInstagram, FaFacebook, FaYoutube, FaSun, FaMoon, FaComments, FaArrowUp, FaTimes, FaCode, FaCloud, FaDatabase, FaBrain, FaDocker, FaAws, FaJava, FaPython, FaReact, FaBriefcase, FaCalendarAlt, FaMapMarkerAlt, FaGraduationCap, FaTrophy, FaDownload, FaEnvelope, FaPhone, FaAward, FaStar, FaCodeBranch, FaChevronLeft, FaChevronRight, FaBook, FaSchool, FaChevronDown, FaChevronUp, FaUser } from 'react-icons/fa';
import { Typewriter } from 'react-simple-typewriter';
import { motion, useScroll, useTransform } from 'framer-motion';
import emailjs from '@emailjs/browser';


export default function Portfolio() {
  const [darkMode, setDarkMode] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [chatLog, setChatLog] = useState([]);
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [activeSection, setActiveSection] = useState('about');
  const [scrollProgress, setScrollProgress] = useState(0);
  const [scrollDirection, setScrollDirection] = useState('down');
  const [lastScrollY, setLastScrollY] = useState(0);
  const [selectedFact, setSelectedFact] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0, isMobile: false });
  const [currentProjectIndex, setCurrentProjectIndex] = useState(0);
  const [expandedAchievements, setExpandedAchievements] = useState({}); // All start collapsed
  const [expandedDescriptions, setExpandedDescriptions] = useState({}); // Track expanded descriptions for mobile
  const [isMobile, setIsMobile] = useState(false);
  const contactForm = useRef(null);
  const chatEndRef = useRef(null);
  const factIconRefs = useRef({});
  const heroRef = useRef(null);
  const projectsRef = useRef(null);
  
  // Parallax scroll effects
  const { scrollYProgress: heroScroll } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  const { scrollYProgress: projectsScroll } = useScroll({
    target: projectsRef,
    offset: ["start end", "end start"]
  });
  const heroY = useTransform(heroScroll, [0, 1], [0, -100]);
  const projectsY = useTransform(projectsScroll, [0, 1], [0, 50]);

 
  const bgGradient = darkMode
    ? 'from-gray-900 via-gray-800 to-gray-900'
    : 'from-red-50 via-red-100 to-red-50';
  const textColor = darkMode ? 'text-white' : 'text-gray-900';

  const qaPairs = {
    "what do you do": "Hi! I’m Sakshi, a software engineer passionate about AI, backend systems, and creativity!",
    "favorite project": "My favorite is LLM Coding Tutor — an AI-based assistant using GPT-4 and LoRA!",
    "tech stack": "Java, Python, C++, React, Node.js, Spring Boot, AWS, Docker, Kubernetes, ML.",
    "hobbies": "Singing, writing, poetry, astronomy, and exploring AI & cybersecurity!",
    "career goal": "To build impactful AI products and push the boundaries of tech for good!"
  };

  // Work Experience Data
  const workExperience = [
    {
      title: "Software Engineer",
      company: "NTT Data Business Solutions",
      location: "Bangalore, India",
      period: "July 2021 - July 2023",
      briefDescription: "Architected and optimized large-scale backend systems handling millions of transactions during peak e-commerce events. Built resilient, scalable infrastructure for India's biggest sales events, reducing API response time by 30% and accelerating release cycles by 40%.",
      detailedDescription: "As a Software Engineer at NTT Data Business Solutions, I played a pivotal role in architecting and optimizing large-scale backend systems that handled millions of transactions during peak e-commerce events. My work focused on building resilient, scalable infrastructure that could handle the massive traffic spikes during India's biggest sales events like Diwali Mega Sales and Big Billion Days. I collaborated closely with product managers, data scientists, and DevOps teams to deliver solutions that not only met performance benchmarks but exceeded them, directly impacting revenue and customer satisfaction.",
      achievements: [
        "Optimized backend services using Python, Java, and AWS DynamoDB, reducing API response time by 30% during peak sales events like Diwali Mega Sales and Big Billion Days. This involved deep performance profiling, query optimization, implementing caching strategies, and database sharding techniques that allowed the system to handle 10x normal traffic without degradation.",
        "Developed comprehensive CI/CD pipelines with Jenkins and Docker, reducing deployment errors by 50% and accelerating release cycles by 40%. I automated the entire deployment process, including automated testing, security scanning, and rollback mechanisms, which enabled the team to deploy multiple times per day with confidence.",
        "Engineered robust data preprocessing pipelines for AI-driven analytics, employing advanced techniques like feature scaling, outlier detection, and Principal Component Analysis (PCA). These pipelines processed terabytes of inventory data daily, enhancing data integrity and enabling 20% more accurate decision-making for inventory management. I worked closely with data scientists to implement feature engineering pipelines that transformed raw data into actionable insights.",
        "Developed and deployed predictive analytics models using XGBoost and Random Forests, collaborating with cross-functional teams to integrate insights into production systems. These models analyzed historical sales patterns, seasonal trends, and market dynamics to predict demand, achieving a 15% reduction in excess inventory storage costs while maintaining optimal stock levels across multiple warehouses.",
        "Led code reviews and mentored junior developers, establishing best practices for code quality, testing, and documentation. I conducted technical training sessions on microservices architecture, cloud computing, and machine learning integration.",
        "Implemented comprehensive monitoring and alerting systems using CloudWatch and custom dashboards, reducing mean time to detection (MTTD) for production issues by 60% and enabling proactive problem resolution."
      ],
      technologies: ["Python", "Java", "AWS DynamoDB", "Jenkins", "Docker", "XGBoost", "Random Forests", "PCA", "CloudWatch", "Microservices", "REST APIs", "NoSQL"]
    },
    {
      title: "Software Developer Intern",
      company: "BOSCH & SIEMENS Home Appliances",
      location: "Bangalore, India",
      period: "Aug 2020 - July 2021",
      briefDescription: "Developed cutting-edge AI and IoT solutions including real-time monitoring dashboards and chatbot systems. Fine-tuned BERT-based models using LoRA, increasing chatbot accuracy by 30% and automating 40% of customer queries.",
      detailedDescription: "During my internship at BOSCH & SIEMENS Home Appliances, I had the opportunity to work on cutting-edge AI and IoT projects that transformed how the company interacted with customers and managed its operations. I was part of a dynamic team building intelligent systems that combined natural language processing, real-time data processing, and predictive analytics. My work spanned from developing real-time monitoring dashboards that tracked critical infrastructure during the COVID-19 pandemic to building sophisticated AI models that powered conversational interfaces. This experience gave me deep exposure to the entire software development lifecycle, from requirements gathering to production deployment, and taught me the importance of building systems that are both technically excellent and user-centric.",
      achievements: [
        "Engineered backend microservices for SmartInventory System using Python, Java, and RabbitMQ, enhancing inventory data retrieval speed by 25% and ensuring real-time updates across 5+ warehouse hubs during peak hours. I designed the message queue architecture to handle asynchronous processing, implemented circuit breakers for fault tolerance, and created APIs that supported both REST and GraphQL interfaces for flexible client integration.",
        "Developed real-time COVID-19 and PodMan monitoring dashboards using React.js, GraphQL, WebSockets, integrating LSTM Autoencoders for real-time anomaly detection. The system processed streaming data from thousands of sensors, reducing response time by 20% and improving fault detection accuracy by 35%. I implemented WebSocket connections for live data streaming, created interactive visualizations using D3.js, and built alerting mechanisms that notified operations teams of anomalies within seconds.",
        "Fine-tuned a BERT-based model for the Bosch Virtual Assistant chatbot using LoRA (Low-Rank Adaptation) and supervised learning, optimizing Cross-Entropy Loss with Optuna hyperparameter tuning. This work involved curating and labeling thousands of customer interaction datasets, implementing data augmentation techniques, and conducting extensive A/B testing. The improvements resulted in a 30% increase in chatbot accuracy and a 25% reduction in misclassification errors, significantly enhancing customer satisfaction scores.",
        "Designed the chatbot's core response-generation algorithms with BM25-based retrieval, GPT-2 fine-tuning, and Reinforcement Learning from Human Feedback (RLHF). I built a hybrid system that combined rule-based logic for common queries with neural network-based responses for complex, contextual interactions. This enabled multi-turn contextual understanding and automated 40% of customer queries on Bosch's product website, reducing support ticket volume and improving response times.",
        "Collaborated with UX designers and product managers to define conversational flows and user experience patterns for the chatbot interface. I conducted user research sessions, analyzed conversation logs to identify pain points, and iteratively improved the system based on feedback.",
        "Implemented comprehensive logging and analytics systems to track chatbot performance metrics, user satisfaction, and conversation quality. I created dashboards that provided insights into common user queries, conversation success rates, and areas for improvement."
      ],
      technologies: ["Python", "Java", "React.js", "GraphQL", "WebSockets", "LSTM", "BERT", "LoRA", "GPT-2", "RabbitMQ", "Optuna", "D3.js", "TensorFlow", "PyTorch", "Docker", "Kubernetes"]
    }
  ];

  // Projects Data
  const projects = [
    {
      id: 1,
      name: "Agent Santa",
      description: "Multi-agent gifting assistant using Fetch.ai and Llama 3, automating end-to-end personalized gift discovery and checkout. Winner of 3rd Place at Cal Hacks 12.0.",
      technologies: ['Fetch.ai', 'Llama 3', 'React', 'Node.js'],
      icon: FaUser,
      iconColor: 'text-maroon-200',
      techColor: 'maroon-300',
      iconStyle: {
        color: '#DC143C',
        filter: 'drop-shadow(0.5px 0 0 #8B0000) drop-shadow(-0.5px 0 0 #8B0000) drop-shadow(0 0.5px 0 #8B0000) drop-shadow(0 -0.5px 0 #8B0000)'
      },
      award: "3rd Place Winner - Cal Hacks 12.0",
      links: [
        { label: 'GitHub', href: 'https://github.com/stripathy1999/agent-santa-calhacks' },
        { label: 'Devpost', href: 'https://devpost.com/software/agent-santa' },
        { label: 'Medium', href: 'https://medium.com/@devamsheth0806/agent-santa-building-an-end-to-end-gift-automation-system-on-fetch-ai-ce40838aa4b5' }
      ]
    },
    {
      id: 2,
      name: "LLM Coding Tutor",
      description: "Evaluating and enhancing LLMs as coding tutors with 92% aggregate accuracy. Research project with automated evaluation pipeline.",
      technologies: ['GPT-4', 'RLHF', 'Python', 'Deep Learning'],
      icon: FaGraduationCap,
      iconColor: 'text-maroon-200',
      techColor: 'maroon-300',
      iconStyle: {
        color: '#1E90FF',
        filter: 'drop-shadow(0.5px 0 0 #00008B) drop-shadow(-0.5px 0 0 #00008B) drop-shadow(0 0.5px 0 #00008B) drop-shadow(0 -0.5px 0 #00008B)'
      },
      links: [
        { label: 'GitHub', href: 'https://github.com/stripathy1999/CodeTutor-All-LLMS' }
      ]
    },
    {
      id: 3,
      name: "Keystroke Anomaly Detector",
      description: "Enhanced anomaly detection using GANs, BiLSTMs, and Isolation Forests with 95% detection accuracy and 0.0% EER.",
      technologies: ['GANs', 'BiLSTM', 'Isolation Forest', 'Python'],
      icon: FaCode,
      iconColor: 'text-maroon-200',
      techColor: 'maroon-300',
      iconStyle: {
        color: '#800080',
        filter: 'drop-shadow(0.5px 0 0 #4B0082) drop-shadow(-0.5px 0 0 #4B0082) drop-shadow(0 0.5px 0 #4B0082) drop-shadow(0 -0.5px 0 #4B0082)'
      },
      links: [
        { label: 'GitHub', href: 'https://github.com/stripathy1999/Keystroke-Anomaly-Detection-Enhanced' }
      ]
    }
  ];

  // Education Timeline Data
  const education = [
    {
      degree: "Master of Science",
      field: "Computer Science",
      university: "San Jose State University",
      location: "San Jose, CA, USA",
      period: "Aug 2023 – May 2026",
      year: "2026",
      gpa: "3.75",
      focus: "AI, Machine Learning, Distributed Systems, Information Retrieval, Database Management Systems, Parallel Computing and Processing, Biometric Security",
      icon: FaGraduationCap,
      iconColor: "text-maroon-200"
    },
    {
      degree: "Bachelor of Technology",
      field: "Computer Science & Engineering",
      university: "Siksha O Anusandhan University",
      location: "Bhubaneswar, India",
      period: "Aug 2017 - Jun 2021",
      year: "2021",
      gpa: "3.57",
      coursework: "Network Fundamentals, Linear Algebra, Statistics, Computer Architecture, Data Structures and Algorithms, Operating Systems, Computer Networks",
      icon: FaGraduationCap,
      iconColor: "text-maroon-200"
    },
    {
      degree: "Higher Secondary",
      field: "CBSE Board",
      university: "Kendriya Vidyalaya",
      location: "Cuttack, India",
      period: "2015 - 2017",
      year: "2017",
      gpa: "87%",
      focus: "CBSE Board, Major in Science - PCMB, 12th Board Exam - 87%",
      icon: FaBook,
      iconColor: "text-maroon-200"
    },
    {
      degree: "Secondary School",
      field: "ICSE Board",
      university: "St. Joseph's Girls' High School",
      location: "Cuttack, India",
      period: "2003 - 2015",
      year: "2015",
      gpa: "92%",
      focus: "10th Board Exam - 92%, Ranked 9th among top 10 ICSE toppers in school",
      icon: FaSchool,
      iconColor: "text-maroon-200"
    }
  ];

  // Scroll to top functionality, progress bar, and scroll direction tracking
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setShowScrollTop(currentScrollY > 300);
      const windowHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scrolled = (currentScrollY / windowHeight) * 100;
      setScrollProgress(scrolled);
      
      // Track scroll direction
      if (currentScrollY > lastScrollY && currentScrollY > 10) {
        setScrollDirection('down');
      } else if (currentScrollY < lastScrollY) {
        setScrollDirection('up');
      }
      setLastScrollY(currentScrollY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // Active section tracking for navbar
  useEffect(() => {
    Events.scrollEvent.register('begin', () => {});
    Events.scrollEvent.register('end', () => {});
    scrollSpy.update();

    const handleScroll = () => {
      const sections = ['about', 'skills', 'experience', 'projects', 'contact'];
      const scrollPosition = window.scrollY + 120; // Adjusted to match scroll offset

      // Check from bottom to top to get the most accurate active section
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop } = element;
          if (scrollPosition >= offsetTop) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    return () => {
      Events.scrollEvent.remove('begin');
      Events.scrollEvent.remove('end');
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Auto-scroll chat to bottom when new messages arrive
  useEffect(() => {
    if (chatEndRef.current && chatOpen) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatLog, chatOpen]);

  // Track window size for responsive carousel
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);


  // Close fun fact tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (selectedFact !== null && !e.target.closest('.fun-fact-icon') && !e.target.closest('.tooltip-container')) {
        setSelectedFact(null);
      }
    };
    if (selectedFact !== null) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [selectedFact]);

  // Initialize EmailJS
  useEffect(() => {
    emailjs.init('ZkiPjdBAsXD1kqrbP');
  }, []);

  // Update tooltip position on scroll or resize (throttled with requestAnimationFrame)
  useEffect(() => {
    if (selectedFact !== null && factIconRefs.current[selectedFact]) {
      let rafId = null;
      
      const updateTooltipPosition = () => {
        const isMobileView = window.innerWidth < 768;
        
        let x, y;
        
        if (isMobileView) {
          // Center on mobile screen - always use viewport center
          // This ensures it's always centered and visible
          x = window.innerWidth / 2;
          y = window.innerHeight / 2;
        } else {
          // Desktop: position relative to icon
          const tooltipWidth = 320; // w-80 = 320px for longer text
          const tooltipHeight = 120; // approximate height for longer descriptions
          const iconElement = factIconRefs.current[selectedFact];
          if (!iconElement) return;
          
          const rect = iconElement.getBoundingClientRect();
          const padding = 10;
          
          // Calculate center x position
          x = rect.left + rect.width / 2;
          // Ensure tooltip doesn't go off left/right edges
          x = Math.max(tooltipWidth / 2 + padding, Math.min(x, window.innerWidth - tooltipWidth / 2 - padding));
          
          // Position below icon with some spacing
          y = rect.top + rect.height + 15;
          // If tooltip would go off bottom, position above icon instead
          if (y + tooltipHeight > window.innerHeight - padding) {
            y = rect.top - tooltipHeight - 15;
          }
          // Ensure tooltip doesn't go off top edge
          y = Math.max(padding, y);
        }
        
        setTooltipPosition({ x, y, isMobile: isMobileView });
      };

      const throttledUpdate = () => {
        if (rafId) return;
        rafId = requestAnimationFrame(() => {
          updateTooltipPosition();
          rafId = null;
        });
      };

      // Update position immediately
      updateTooltipPosition();

      // Update on scroll and resize (throttled)
      window.addEventListener('scroll', throttledUpdate, { passive: true });
      window.addEventListener('resize', throttledUpdate, { passive: true });

      return () => {
        if (rafId) cancelAnimationFrame(rafId);
        window.removeEventListener('scroll', throttledUpdate);
        window.removeEventListener('resize', throttledUpdate);
      };
    }
  }, [selectedFact]);



  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Knowledge Base - Portfolio Information
  const portfolioData = {
    name: "Sakshi",
    about: "I'm a software engineer passionate about AI, backend systems, and creativity. I love building things that matter and expressing myself through music and writing. I transform complex problems into elegant code and complex emotions into beautiful art.",
    hobbies: ["Singing", "Writing", "Poetry", "Astronomy", "AI & Cybersecurity exploration"],
    careerGoal: "To build impactful AI products and push the boundaries of tech for good!",
    skills: {
      programmingLanguages: ['Java', 'Python', 'C', 'C++', 'JavaScript', 'SQL'],
      frameworks: ['React', 'Node.js', 'Spring Boot', 'Django', 'Flask', 'PyTorch', 'TensorFlow'],
      cloud: ['AWS', 'Docker', 'Kubernetes', 'Jenkins', 'CI/CD'],
      databases: ['DynamoDB', 'MongoDB', 'PostgreSQL', 'MySQL'],
      ai: ['Machine Learning', 'Deep Learning', 'LLMs', 'GPT-4', 'BERT', 'XGBoost', 'Random Forests', 'GANs', 'LSTM', 'LoRA', 'RLHF']
    },
    projects: [
      {
        name: "Agent Santa",
        description: "Multi-agent gifting assistant using Fetch.ai and Llama 3, automating end-to-end personalized gift discovery and checkout.",
        technologies: ['Fetch.ai', 'Llama 3', 'React', 'Node.js'],
        award: "3rd Place Winner - Cal Hacks 12.0"
      },
      {
        name: "LLM Coding Tutor",
        description: "Evaluating and enhancing LLMs as coding tutors with 92% aggregate accuracy. Research project with automated evaluation pipeline.",
        technologies: ['GPT-4', 'RLHF', 'Python', 'Deep Learning']
      },
      {
        name: "Keystroke Anomaly Detector",
        description: "Enhanced anomaly detection using GANs, BiLSTMs, and Isolation Forests with 95% detection accuracy and 0.0% EER.",
        technologies: ['GANs', 'BiLSTM', 'Isolation Forest', 'Python']
      }
    ],
    education: [
      {
        degree: "Master of Science",
        field: "Computer Science",
        university: "San Jose State University",
        period: "Aug 2023 – May 2026",
        gpa: "3.75",
        focus: "AI, Machine Learning, Distributed Systems, Information Retrieval, Database Management Systems, Parallel Computing and Processing, Biometric Security"
      },
      {
        degree: "Bachelor of Technology",
        field: "Computer Science & Engineering",
        university: "Siksha O Anusandhan University",
        period: "Aug 2017 - Jun 2021",
        gpa: "3.57",
        coursework: "Network Fundamentals, Linear Algebra, Statistics, Computer Architecture, Data Structures and Algorithms, Operating Systems, Computer Networks"
      }
    ]
  };

  // Intelligent chatbot function that learns from portfolio data
  const getBotResponse = (userMessage) => {
    const msg = userMessage.toLowerCase().trim();
    
    // Greeting patterns
    if (msg.match(/^(hi|hello|hey|greetings|good morning|good afternoon|good evening)/)) {
      return `Hello! I'm ${portfolioData.name}'s portfolio assistant. How can I help you learn more about my work, skills, projects, or experience?`;
    }

    // About me questions
    if (msg.match(/(who are you|what do you do|tell me about yourself|about you|introduce)/)) {
      return `Hi! ${portfolioData.about} ${portfolioData.careerGoal} Feel free to ask me about my work, projects, or interests!`;
    }

    // Skills questions
    if (msg.match(/(what.*skill|your skill|technologies|tech stack|programming|language|framework|what can you do|expertise)/)) {
      if (msg.match(/(programming|language|code)/)) {
        return `I work with several programming languages: ${portfolioData.skills.programmingLanguages.join(', ')}. I'm most comfortable with Python and Java for backend development, and JavaScript for frontend work.`;
      }
      if (msg.match(/(framework|library|react|django|spring)/)) {
        return `I use various frameworks and libraries including ${portfolioData.skills.frameworks.join(', ')}. React is great for frontend work, while Spring Boot and Django are my go-to for backend services.`;
      }
      if (msg.match(/(cloud|aws|docker|kubernetes|devops|ci\/cd)/)) {
        return `For cloud and DevOps, I work with ${portfolioData.skills.cloud.join(', ')}. I've built CI/CD pipelines and deployed applications on AWS using containerization.`;
      }
      if (msg.match(/(database|db|dynamodb|mysql|postgres)/)) {
        return `I've worked with several databases including ${portfolioData.skills.databases.join(', ')}. I've used DynamoDB extensively in production environments for scalable applications.`;
      }
      if (msg.match(/(ai|machine learning|ml|deep learning|llm|gpt|bert|neural)/)) {
        return `AI and Machine Learning are my passions! I work with ${portfolioData.skills.ai.join(', ')}. I've fine-tuned LLMs, built neural networks, and deployed ML models in production.`;
      }
      
      return `I have a diverse set of technical skills! I'm proficient in programming languages like ${portfolioData.skills.programmingLanguages.slice(0, 4).join(', ')}, work with frameworks such as ${portfolioData.skills.frameworks.slice(0, 3).join(', ')}, and have extensive experience in AI/ML with ${portfolioData.skills.ai.slice(0, 4).join(', ')}. I also have cloud and DevOps experience with ${portfolioData.skills.cloud.slice(0, 3).join(', ')}. Would you like to know more about any specific area?`;
    }

    // Work experience questions
    if (msg.match(/(work|experience|job|employment|career|ntt|bosch|siemens|where.*work|what.*job|where.*you.*work)/)) {
      if (msg.match(/(ntt|ntt data)/)) {
        const ntt = workExperience[0];
        return `I worked at ${ntt.company} as a ${ntt.title} from ${ntt.period}. ${ntt.description} During my time there, I ${ntt.achievements[0].toLowerCase()} and also ${ntt.achievements[1].toLowerCase()}. I worked with technologies like ${ntt.technologies.slice(0, 5).join(', ')}.`;
      }
      if (msg.match(/(bosch|siemens)/)) {
        const bosch = workExperience[1];
        return `I worked as a ${bosch.title} at ${bosch.company} from ${bosch.period}. ${bosch.description} Some highlights include ${bosch.achievements[0].toLowerCase()}. I used technologies such as ${bosch.technologies.slice(0, 5).join(', ')}.`;
      }
      
      // Recent/current work
      if (msg.match(/(recent|current|latest|most recent|where.*currently)/)) {
        const recent = workExperience[0]; // Most recent is first in array
        return `My most recent role was as a ${recent.title} at ${recent.company} (${recent.period}). ${recent.description} I ${recent.achievements[0].toLowerCase()}. It was a great experience working with ${recent.technologies.slice(0, 4).join(', ')}.`;
      }
      
      // General work experience question
      let response = "I've had some great work experiences! ";
      workExperience.forEach((exp, idx) => {
        response += `${idx === 0 ? 'Most recently' : 'Before that'}, I worked as ${exp.title} at ${exp.company} (${exp.period}). ${exp.description} `;
      });
      return response + "Would you like to know more about any specific role?";
    }

    // Projects questions - Handle contextual questions first
    if (msg.match(/(project|what.*built|what.*work.*on|portfolio project|github)/)) {
      // Most recent, proud of, favorite, best project - Agent Santa (has award and is first)
      if (msg.match(/(most recent|latest|recent|proud|favorite|best|favourite)/)) {
        const proj = portfolioData.projects[0]; // Agent Santa
        return `I'm really proud of ${proj.name}! ${proj.description} ${proj.award ? `It won ${proj.award} at Cal Hacks 12.0, which was such an exciting achievement!` : ''} I built it using ${proj.technologies.join(', ')}. It's definitely one of my most impactful projects so far.`;
      }
      
      // Specific project mentions
      if (msg.match(/(agent santa|santa|gift)/)) {
        const proj = portfolioData.projects[0];
        return `${proj.name} is a project I'm really excited about! ${proj.description} ${proj.award ? `It won ${proj.award}, which was an amazing experience!` : ''} I used ${proj.technologies.join(', ')} to build it.`;
      }
      if (msg.match(/(llm|coding tutor|code tutor|tutor)/)) {
        const proj = portfolioData.projects[1];
        return `${proj.name} is a research project I worked on. ${proj.description} I built it using ${proj.technologies.join(', ')}. It was a great learning experience in the field of AI and education.`;
      }
      if (msg.match(/(keystroke|anomaly|detector|security|biometric)/)) {
        const proj = portfolioData.projects[2];
        return `${proj.name} is one of my security-focused projects. ${proj.description} I implemented it using ${proj.technologies.join(', ')}. It was a challenging project that taught me a lot about anomaly detection.`;
      }
      
      // General projects question - give a conversational overview
      if (msg.match(/(tell.*about.*project|describe.*project|what.*project)/)) {
        return `I've worked on several exciting projects! My most recent is Agent Santa - a multi-agent gifting assistant that won 3rd Place at Cal Hacks 12.0. I've also worked on an LLM Coding Tutor with 92% accuracy, and a Keystroke Anomaly Detector with 95% detection accuracy. Would you like to know more about any specific one?`;
      }
      
      // List all projects (only if explicitly asked)
      let response = "Here are my featured projects:\n\n";
      portfolioData.projects.forEach((proj, idx) => {
        response += `${idx + 1}. ${proj.name}${proj.award ? ` (${proj.award})` : ''}\n   ${proj.description}\n   Technologies: ${proj.technologies.join(', ')}\n\n`;
      });
      return response.trim();
    }

    // Education questions
    if (msg.match(/(education|university|college|degree|graduate|master|bachelor|study|sjsu|school|where.*study|where.*graduate)/)) {
      if (msg.match(/(master|ms|graduate|sjsu|san jose|current|currently)/)) {
        const edu = portfolioData.education[0];
        return `I'm currently pursuing my ${edu.degree} in ${edu.field} from ${edu.university} (${edu.period}) with a GPA of ${edu.gpa}. My focus areas include ${edu.focus}. It's been an amazing journey diving deep into AI and distributed systems!`;
      }
      if (msg.match(/(bachelor|btech|undergraduate)/)) {
        const edu = portfolioData.education[1];
        return `I completed my ${edu.degree} in ${edu.field} from ${edu.university} (${edu.period}) with a GPA of ${edu.gpa}. My coursework included ${edu.coursework}. It gave me a strong foundation in computer science fundamentals.`;
      }
      
      // General education question
      const currentEdu = portfolioData.education[0];
      const prevEdu = portfolioData.education[1];
      return `I'm currently working on my ${currentEdu.degree} in ${currentEdu.field} at ${currentEdu.university}, focusing on ${currentEdu.focus.split(', ').slice(0, 2).join(' and ')}. Previously, I completed my ${prevEdu.degree} from ${prevEdu.university} with a GPA of ${prevEdu.gpa}.`;
    }

    // Hobbies/Interests
    if (msg.match(/(hobby|hobbies|interest|what.*like|what.*enjoy|passion|free time|what do you do for fun|what.*do.*outside.*work)/)) {
      return `When I'm not coding, I love ${portfolioData.hobbies.slice(0, 3).join(', ').toLowerCase()}. ${portfolioData.about} It's important to me to balance technical work with creative expression!`;
    }

    // Career goals
    if (msg.match(/(goal|aspiration|future|dream|what.*want|where.*see)/)) {
      return portfolioData.careerGoal;
    }

    // Achievement/Awards
    if (msg.match(/(award|achievement|win|winner|accomplishment|recognized)/)) {
      return `I won 3rd Place at Cal Hacks 12.0 with my project Agent Santa - a multi-agent gifting assistant! You can learn more about it in my projects section.`;
    }

    // Contact/Collaboration
    if (msg.match(/(contact|reach|email|collaborate|work together|hire|opportunity)/)) {
      return "Feel free to reach out via the contact form below! I'm always open to discussing new projects, collaborations, or opportunities.";
    }

    // Help
    if (msg.match(/(help|what can you|what.*ask|how.*help)/)) {
      return "I can answer questions about:\n- Skills and technologies\n- Work experience\n- Projects\n- Education\n- Hobbies and interests\n- Career goals\n- Contact information\n\nTry asking about any of these topics!";
    }

    // Default fallback
    return "I'm not sure I understand that question. Try asking about my skills, work experience, projects, education, or hobbies. You can also ask 'help' to see what I can answer!";
  };

  const handleChatSubmit = (e) => {
    e.preventDefault();
    const userMsg = chatInput.trim();
    if (!userMsg) return;
    const botReply = getBotResponse(userMsg);
    setChatLog([...chatLog, { from: 'user', text: userMsg }, { from: 'bot', text: botReply }]);
    setChatInput('');
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: '', message: '' });

    if (!contactForm.current) {
      setFormStatus({ 
        type: 'error', 
        message: 'Form reference error. Please refresh the page.' 
      });
      setIsSubmitting(false);
      return;
    }

    // Validate form fields
    const formData = new FormData(contactForm.current);
    const userName = formData.get('user_name');
    const userEmail = formData.get('user_email');
    const message = formData.get('message');

    if (!userName || !userEmail || !message) {
      setFormStatus({ 
        type: 'error', 
        message: 'Please fill in all required fields.' 
      });
      setIsSubmitting(false);
      return;
    }

    try {
      // Prepare template parameters
      // Note: Make sure 'To Email' is set in your EmailJS template dashboard (template_z50wfts)
      const templateParams = {
        user_name: userName,
        user_email: userEmail,
        message: message,
      };

      // Send the form using EmailJS
      const result = await emailjs.send(
        'service_ju6u169',
        'template_z50wfts',
        templateParams,
        'ZkiPjdBAsXD1kqrbP'
      );

      console.log('EmailJS success:', result);
      setFormStatus({ 
        type: 'success', 
        message: 'Thank you! Your message has been sent successfully. I\'ll get back to you soon!' 
      });
      contactForm.current.reset();
    } catch (error) {
      console.error('EmailJS error details:', error);
      console.error('Full error object:', JSON.stringify(error, null, 2));
      
      let errorMessage = 'Failed to send message. Please try again later or reach out via social media.';
      
      // Extract more detailed error information
      if (error.text) {
        errorMessage = `Error: ${error.text}`;
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      } else if (error.status) {
        errorMessage = `Error ${error.status}: ${error.text || 'Failed to send message. Please check your EmailJS configuration.'}`;
      } else if (typeof error === 'string') {
        errorMessage = `Error: ${error}`;
      }
      
      // Log the full error for debugging
      console.error('Full error:', error);
      
      setFormStatus({ 
        type: 'error', 
        message: errorMessage
      });
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <div className={`relative min-h-screen bg-gradient-to-br ${bgGradient} ${textColor} overflow-x-hidden`}>
      {/* Scroll Progress Bar */}
      <div className="fixed top-0 left-0 w-full h-1 bg-gray-800 z-50">
        <motion.div
          className="h-full bg-gradient-to-r from-maroon-200 via-maroon-200 to-maroon-200"
          style={{ width: `${scrollProgress}%` }}
          transition={{ duration: 0.1 }}
        />
      </div>

      {/* Animated Background Gradient Orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        {/* Animated gradient orbs */}
        <motion.div
          animate={{
            x: [0, 100, 0],
            y: [0, 50, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className={`absolute top-20 left-10 w-96 h-96 rounded-full ${darkMode ? 'bg-maroon-300' : 'bg-white'} ${darkMode ? 'opacity-30' : 'opacity-80'} blur-3xl`}
        />
        <motion.div
          animate={{
            x: [0, -80, 0],
            y: [0, 100, 0],
            scale: [1, 1.3, 1],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
          className={`absolute bottom-20 right-10 w-96 h-96 rounded-full ${darkMode ? 'bg-maroon-300' : 'bg-white'} ${darkMode ? 'opacity-30' : 'opacity-80'} blur-3xl`}
        />
        <motion.div
          animate={{
            x: [0, 120, 0],
            y: [0, -60, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 30,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 4
          }}
          className={`absolute top-1/2 left-1/3 w-80 h-80 rounded-full ${darkMode ? 'bg-maroon-300' : 'bg-white'} ${darkMode ? 'opacity-25' : 'opacity-70'} blur-3xl`}
        />
        <motion.div
          animate={{
            x: [0, -100, 0],
            y: [0, 80, 0],
            scale: [1, 1.25, 1],
          }}
          transition={{
            duration: 22,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
          className={`absolute top-1/4 right-1/4 w-72 h-72 rounded-full ${darkMode ? 'bg-maroon-300' : 'bg-white'} ${darkMode ? 'opacity-25' : 'opacity-70'} blur-3xl`}
        />
        
        {/* Floating animated shapes */}
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            animate={{
              y: [0, -40, 0],
              x: [0, (i % 2 === 0 ? 30 : -30), 0],
              rotate: [0, 360],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 10 + i * 1.5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 0.3
            }}
            className={`absolute ${
              ['top-20', 'top-40', 'top-2/3', 'bottom-32', 'top-1/3', 'bottom-20', 'top-3/4', 'bottom-1/3'][i]
            } ${
              ['left-1/6', 'left-1/3', 'right-1/4', 'left-2/3', 'right-1/6', 'left-1/2', 'right-1/3', 'left-3/4'][i]
            } w-40 h-40 md:w-48 md:h-48 rounded-full ${darkMode ? 'bg-maroon-300' : 'bg-white'} ${darkMode ? 'opacity-40' : 'opacity-85'} blur-2xl`}
            style={{
              filter: `blur(60px)`,
            }}
          />
        ))}
      </div>

      <Particles
        options={{
          background: { color: darkMode ? '#1f2937' : '#fef2f2' },
          particles: {
            number: { 
              value: 80,
              density: {
                enable: true,
                value_area: 800
              }
            },
            color: { value: darkMode ? ['#ffffff', '#60a5fa', '#a78bfa', '#34d399'] : ['#0f172a', '#3b82f6', '#6366f1', '#10b981'] },
            size: { 
              value: { min: 1, max: 3 },
              animation: {
                enable: true,
                speed: 2,
                size_min: 0.5,
                sync: false
              }
            },
            move: { 
              enable: true,
              speed: { min: 0.5, max: 2 },
              direction: "none",
              random: true,
              straight: false,
              out_mode: "out",
              bounce: false,
              attract: {
                enable: true,
                rotateX: 600,
                rotateY: 1200
              }
            },
            links: { 
              enable: true, 
              color: darkMode ? '#60a5fa' : '#3b82f6', 
              opacity: 0.3,
              distance: 150,
              triangles: {
                enable: true,
                opacity: 0.1
              }
            },
            interactivity: {
              detect_on: "canvas",
              events: { 
                onHover: { enable: true, mode: ['repulse', 'bubble'] },
                onClick: { enable: true, mode: 'push' },
                resize: true
              },
              modes: {
                repulse: {
                  distance: 100,
                  duration: 0.4
                },
                bubble: {
                  distance: 200,
                  size: 4,
                  duration: 2,
                  opacity: 0.6
                },
                push: {
                  particles_nb: 4
                }
              }
            },
          },
        }}
        className="absolute inset-0 z-0 opacity-60"
      />
      {/* Scroll to top button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-24 right-6 bg-maroon-200 text-white p-3 rounded-full z-20 hover:opacity-90 shadow-lg transition-all duration-150 hover:scale-110"
          title="Scroll to top"
        >
          <FaArrowUp size={20} />
        </button>
      )}

      {/* Chatbot toggle */}
      <button
        onClick={() => setChatOpen(!chatOpen)}
        className="fixed bottom-6 right-6 bg-maroon-200 text-white p-3 rounded-full z-20 hover:opacity-90 transition-all duration-150 hover:scale-110 shadow-lg"
        title="Chat with Sakshi"
      >
        <FaComments size={24} />
      </button>


      {/* Chatbot window */}
      {chatOpen && (
        <div className={`fixed bottom-20 right-2 md:right-6 w-[calc(100%-1rem)] md:w-80 lg:w-96 max-w-sm ${darkMode ? 'bg-gray-800 text-white' : 'bg-gray-200 text-charcoal'} rounded-lg shadow-2xl z-30 animate-in fade-in slide-in-from-bottom-5 duration-300`}>
          <div className="p-3 bg-maroon-200 text-white rounded-t-lg flex justify-between items-center">
            <span className="font-bold">💬 Chat with Sakshi</span>
            <button
              onClick={() => setChatOpen(false)}
              className="hover:opacity-80 rounded-full p-1 transition-colors"
              title="Close chat"
            >
              <FaTimes size={16} />
            </button>
          </div>
          <div className="p-3 max-h-80 overflow-y-auto">
            {chatLog.length === 0 && (
              <div className={`text-center text-sm ${darkMode ? 'text-white' : 'text-gray-500'} py-4`}>
                Ask me about my work, skills, projects, or hobbies!
              </div>
            )}
            {chatLog.map((entry, idx) => (
              <div key={idx} className={`mb-3 ${entry.from === 'user' ? 'text-right' : 'text-left'}`}>
                <span className={`inline-block px-4 py-2 rounded-lg max-w-[80%] ${entry.from === 'user' ? (darkMode ? 'bg-maroon-200 text-white' : 'bg-maroon-200 text-white') : (darkMode ? 'bg-gray-700 text-white' : 'bg-gray-200 text-charcoal')}`}>
                  {entry.text}
                </span>
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>
          <form onSubmit={handleChatSubmit} className={`flex border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <input
              type="text"
              className={`flex-grow p-3 rounded-bl-lg focus:outline-none focus:ring-2 focus:ring-maroon-200 ${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-200 text-charcoal'}`}
              placeholder="Ask me anything..."
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
            />
            <button type="submit" className="px-4 bg-maroon-200 text-white rounded-br-lg hover:opacity-90 transition-all duration-150 font-semibold">Send</button>
          </form>
        </div>
      )}

      {/* Navbar */}
      <nav
        className={`fixed top-0 w-full flex items-center justify-between px-3 md:px-6 py-2 md:py-3 z-40 border-b backdrop-blur-md transition-colors duration-300 ${
          darkMode
            ? 'bg-black/90 border-white/10 text-white'
            : 'bg-white/85 border-gray-200 text-gray-900 shadow-sm'
        }`}
      >
        <div className="flex items-center space-x-2 md:space-x-4">
        {['about', 'skills', 'experience', 'projects', 'contact'].map((section) => (
          <Link
            key={section}
            to={section}
            smooth
            duration={500}
            offset={-80}
            spy
            className={`cursor-pointer capitalize transition-all duration-150 px-2 md:px-3 py-1 rounded-md text-sm md:text-base ${
              activeSection === section
                  ? (darkMode ? 'text-white font-semibold bg-white/15' : 'text-maroon-200 font-semibold bg-white/15')
                  : (darkMode ? 'hover:text-white hover:bg-white/10' : 'hover:text-maroon-200 hover:bg-white/10')
            }`}
          >
            {section}
          </Link>
        ))}
        </div>
        <button
          onClick={() => setDarkMode(!darkMode)}
          className={`ml-3 flex items-center justify-center rounded-full p-1.5 md:p-2 border text-xs md:text-sm transition-all duration-200 ${
            darkMode
              ? 'bg-gray-900/70 border-gray-700 text-yellow-300 hover:bg-gray-800'
              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100'
          }`}
          title="Toggle dark/light mode"
        >
          {darkMode ? <FaSun size={16} /> : <FaMoon size={16} />}
        </button>
      </nav>

      {/* Hero section with Typewriter + Photo */}
      <motion.header 
        ref={heroRef}
        id="about" 
        style={{ y: heroY }}
        className="min-h-[calc(100vh-80px)] md:min-h-[70vh] pt-28 md:pt-16 z-10 relative flex flex-col md:flex-row items-center justify-center md:justify-center max-w-6xl mx-auto px-4 scroll-mt-24"
      >
        <div className="text-center md:text-left md:w-1/2">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
          >
            <h1 className={`text-4xl md:text-5xl font-bold mb-4 ${darkMode ? 'text-white' : ''}`} style={{ fontFamily: 'Arial, sans-serif' }}>
              Hi, I'm Sakshi Tripathy
            </h1>
            <h2 className={`text-2xl md:text-3xl font-semibold mb-4 text-left ${darkMode ? 'text-white' : 'text-maroon-200'}`} style={{ fontFamily: 'Arial, sans-serif' }}>
              <Typewriter
                words={['Software Engineer', 'Backend Developer', 'AI Enthusiast', 'Musician', 'Singer', 'Artist', 'Stargazer']}
                loop
                cursor
                cursorStyle="|"
                typeSpeed={22}
                deleteSpeed={50}
                delaySpeed={2000}
              />
            </h2>
            <p className={`text-xl mt-2 mb-6 italic text-left ${darkMode ? 'text-white' : ''}`} style={{ fontFamily: 'Arial, sans-serif' }}>
              Welcome to my world of code, creativity, and innovation.
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-3 mt-4 justify-start">
              <motion.a
                href="/resume.pdf"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center gap-2 px-6 py-3 bg-maroon-200 text-white rounded-lg font-semibold hover:opacity-90 transition-all duration-150 shadow-lg"
              >
                <FaDownload />
                Download Resume
              </motion.a>
              <Link to="contact" smooth duration={500} offset={-80}>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-transparent border-2 border-maroon-200 text-maroon-200 rounded-lg font-semibold hover:bg-maroon-200 hover:text-white transition-all duration-150"
                >
                  Get In Touch
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
        <div className="md:w-1/3 mt-8 md:mt-0 md:ml-8 flex justify-center">
          <motion.img
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            src={localStorage.getItem("profilePhotoURL") || "/images/Sakshi1.png"}
            alt="Sakshi Tripathy"
            className="w-64 h-64 md:w-72 md:h-72 object-cover object-[65%_22%] rounded-full shadow-[0_20px_60px_rgba(0,0,0,0.6)] border-[6px] border-white/90 ring-[10px] ring-maroon-300/30 scale-65 hover:scale-95 md:hover:scale-100 transition-transform duration-200"
          />
        </div>
      </motion.header>

      {/* Stats Section */}
      <motion.section 
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 30 }}
        viewport={{ once: false, amount: 0 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="m-6 md:mt-0 md:mb-10 md:mx-10 z-10 relative"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { 
              label: 'Years Experience', 
              value: '3+', 
              icon: FaBriefcase, 
              outlineColor: '#654321',
              style: { 
                color: '#8B4513',
                filter: 'drop-shadow(0.5px 0 0 #654321) drop-shadow(-0.5px 0 0 #654321) drop-shadow(0 0.5px 0 #654321) drop-shadow(0 -0.5px 0 #654321)'
              }
            },
            { 
              label: 'Projects', 
              value: '10+', 
              icon: FaCode, 
              outlineColor: darkMode ? '#cccccc' : '#5a0017',
              style: { 
                color: darkMode ? '#ffffff' : '#800020',
                filter: darkMode 
                  ? 'drop-shadow(0.5px 0 0 #cccccc) drop-shadow(-0.5px 0 0 #cccccc) drop-shadow(0 0.5px 0 #cccccc) drop-shadow(0 -0.5px 0 #cccccc)'
                  : 'drop-shadow(0.5px 0 0 #5a0017) drop-shadow(-0.5px 0 0 #5a0017) drop-shadow(0 0.5px 0 #5a0017) drop-shadow(0 -0.5px 0 #5a0017)'
              }
            },
            { 
              label: 'Technologies', 
              value: '20+', 
              icon: FaBrain, 
              outlineColor: '#C2185B',
              style: { 
                color: '#FF69B4',
                filter: 'drop-shadow(0.5px 0 0 #C2185B) drop-shadow(-0.5px 0 0 #C2185B) drop-shadow(0 0.5px 0 #C2185B) drop-shadow(0 -0.5px 0 #C2185B) drop-shadow(0.3px 0.3px 0.3px rgba(194, 24, 91, 0.4)) drop-shadow(-0.3px -0.3px 0.3px rgba(194, 24, 91, 0.4)) drop-shadow(0.3px -0.3px 0.3px rgba(194, 24, 91, 0.4)) drop-shadow(-0.3px 0.3px 0.3px rgba(194, 24, 91, 0.4)) drop-shadow(0.2px 0.2px 0.2px rgba(255, 105, 180, 0.5)) drop-shadow(-0.2px -0.2px 0.2px rgba(255, 105, 180, 0.5))'
              }
            },
            { 
              label: 'Awards', 
              value: '1', 
              icon: FaTrophy, 
              outlineColor: '#DAA520',
              style: { 
                color: '#FFD700',
                filter: 'drop-shadow(0.5px 0 0 #DAA520) drop-shadow(-0.5px 0 0 #DAA520) drop-shadow(0 0.5px 0 #DAA520) drop-shadow(0 -0.5px 0 #DAA520)'
              }
            }
          ].map((stat, idx) => {
            const IconComponent = stat.icon;
            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: false, amount: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="text-center cursor-default"
              >
                <IconComponent 
                  className="text-4xl mx-auto mb-3" 
                  style={stat.style}
                />
                <div className={`text-3xl font-bold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{stat.value}</div>
                <div className={`text-sm ${darkMode ? 'text-white' : 'text-gray-600'}`}>{stat.label}</div>
              </motion.div>
            );
          })}
        </div>


      {/* Tooltip Portal - Desktop tooltip or Mobile bottom drawer */}
      {selectedFact !== null && createPortal(
        <>
          {/* Backdrop overlay */}
          <div
            onClick={(e) => {
              e.stopPropagation();
              setSelectedFact(null);
            }}
            className="fixed inset-0 bg-black bg-opacity-30"
            style={{ zIndex: 9999 }}
          />
          
          {/* Mobile: Bottom Drawer Modal */}
          {tooltipPosition.isMobile ? (
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
              onClick={(e) => e.stopPropagation()}
              className={`fixed bottom-0 left-0 right-0 rounded-t-3xl shadow-2xl ${
                darkMode 
                  ? 'bg-gray-800 border-t-2 border-gray-600' 
                  : 'bg-white border-t-2 border-gray-300'
              }`}
              style={{ 
                zIndex: 10000,
                maxHeight: '85vh',
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch'
              }}
            >
              {/* Drawer Handle */}
              <div className="flex justify-center pt-3 pb-2">
                <div className={`w-12 h-1.5 rounded-full ${darkMode ? 'bg-gray-600' : 'bg-gray-300'}`} />
              </div>
              
              {/* Close Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedFact(null);
                }}
                className={`absolute top-3 right-4 w-8 h-8 rounded-full flex items-center justify-center ${
                  darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'
                } transition-colors shadow-lg z-30`}
                aria-label="Close"
              >
                <FaTimes size={16} className={darkMode ? 'text-white' : 'text-gray-700'} />
              </button>
              
              {/* Content */}
              <div className="px-6 pb-8 pt-4">
                <p className={`text-base text-center font-medium leading-relaxed ${darkMode ? 'text-white' : 'text-gray-900'}`} style={{ 
                  wordBreak: 'break-word', 
                  overflowWrap: 'break-word', 
                  hyphens: 'auto', 
                  WebkitHyphens: 'auto', 
                  margin: 0, 
                  padding: 0, 
                  width: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                  whiteSpace: 'normal',
                  display: 'block',
                  lineHeight: '1.7',
                  textAlign: 'center'
                }}>
                  {[
                    { fact: 'I believe great ideas usually arrive after coffee, during walks, or while explaining something to someone else.' },
                    { fact: 'I\'m deeply fascinated by astronomy, quantum physics, astrophysics and big unanswered questions — the universe humbles me more than any compiler error.' },
                    { fact: 'I write poetry and stories, usually late at night, often about humans, systems, philosophies and the strange overlap between them.' },
                    { fact: 'Outside tech, I\'m a singer and performer — I\'ve led college bands, competed, and performed live on stage.' },
                    { fact: 'I care deeply about humanitarian causes, especially supporting women, the elderly, and animals.' },
                    { fact: 'I enjoy dark humor and jokes, and enjoy deep talks.' }
                  ][selectedFact]?.fact}
                </p>
              </div>
            </motion.div>
          ) : (
            /* Desktop: Tooltip with fixed positioning */
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.8 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.8 }}
              transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
              onClick={(e) => e.stopPropagation()}
              className={`tooltip-container fixed md:w-80 rounded-lg shadow-2xl relative p-4 ${
                darkMode 
                  ? 'bg-gray-800 border-2 border-gray-600 shadow-[0_0_20px_rgba(96,165,250,0.3)]' 
                  : 'bg-white border-2 border-gray-300 shadow-[0_0_20px_rgba(59,130,246,0.2)]'
              }`}
              style={{ 
                zIndex: 10000,
                position: 'fixed',
                left: `${tooltipPosition.x}px`,
                top: `${tooltipPosition.y}px`,
                transform: 'translate(-50%, 0)',
                pointerEvents: 'auto'
              }}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedFact(null);
                }}
                className={`absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center ${
                  darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'
                } transition-colors shadow-lg z-30`}
                aria-label="Close tooltip"
              >
                <FaTimes size={12} className={darkMode ? 'text-white' : 'text-gray-700'} />
              </button>
              <div style={{ 
                width: '100%', 
                boxSizing: 'border-box',
                paddingRight: '0',
                flex: '1 1 auto',
                overflow: 'visible'
              }}>
              <p className={`text-sm md:text-sm text-center font-medium leading-relaxed ${darkMode ? 'text-white' : 'text-gray-900'}`} style={{ 
                wordBreak: 'break-word', 
                overflowWrap: 'break-word', 
                hyphens: 'auto', 
                WebkitHyphens: 'auto', 
                margin: 0, 
                padding: 0, 
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                whiteSpace: 'normal',
                display: 'block',
                lineHeight: '1.7',
                textAlign: 'center'
              }}>
                {[
                  { fact: 'I believe great ideas usually arrive after coffee, during walks, or while explaining something to someone else.' },
                  { fact: 'I\'m deeply fascinated by astronomy, quantum physics, astrophysics and big unanswered questions — the universe humbles me more than any compiler error.' },
                  { fact: 'I write poetry and stories, usually late at night, often about humans, systems, philosophies and the strange overlap between them.' },
                  { fact: 'Outside tech, I\'m a singer and performer — I\'ve led college bands, competed, and performed live on stage.' },
                  { fact: 'I care deeply about humanitarian causes, especially supporting women, the elderly, and animals.' },
                  { fact: 'I enjoy dark humor and jokes, and enjoy deep talks.' }
                ][selectedFact]?.fact}
              </p>
              </div>
            </motion.div>
          )}
        </>,
        document.body
      )}

      </motion.section>

      {/* Sections */}
      <section className="grid md:grid-cols-2 gap-6 m-6 md:mt-16 md:mb-10 md:mx-10 z-10 relative">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -30 }}
          viewport={{ once: false, amount: 0 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className={`backdrop-blur p-6 rounded-xl border transition-all duration-150 ${
            darkMode 
              ? 'bg-white/5 hover:bg-white/10 border-white/10' 
              : 'bg-gray-50 border-gray-200'
          }`}
        >
          <div className="flex items-center justify-between gap-3 mb-4">
            <h2 className="text-2xl font-semibold">About Me</h2>
            <motion.div
              initial={{ opacity: 0, scale: 0.8, x: 10 }}
              whileInView={{ opacity: 1, scale: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3 }}
              className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-maroon-300/10 border border-maroon-300/40"
            >
              <span className="w-7 h-7 rounded-full overflow-hidden border border-white/70 shadow-md">
                <img
                  src="/images/image.png"
                  alt="Sakshi in a candid moment"
                  className="w-full h-full object-cover"
                />
              </span>
              <span className={`text-xs font-medium ${darkMode ? 'text-white' : 'text-charcoal'}`}>
                A little more of my world ✨
              </span>
            </motion.div>
          </div>
          <p className={`mb-4 ${darkMode ? 'text-white' : 'text-gray-700'}`}>
            I'm a software engineer passionate about AI, backend systems, and creativity.
            I love building things that matter and expressing myself through music and writing.
          </p>
          <p className={`mb-4 ${darkMode ? 'text-white' : 'text-gray-700'}`}>
            Crafting intelligent solutions where <span className={`font-semibold ${darkMode ? 'text-white' : 'text-maroon-200'}`}>AI meets creativity</span>. 
            When I'm not building scalable backend systems or fine-tuning LLMs, you'll find me composing melodies, 
            writing poetry, or exploring the cosmos through my telescope.
          </p>
          <p className={`${darkMode ? 'text-white' : 'text-gray-700'}`}>
            I transform complex problems into elegant code and complex emotions into beautiful art. 
            Passionate about pushing the boundaries of technology while staying connected to my creative roots.
          </p>
          {/* Warm side photo on mobile */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-4 flex md:hidden justify-center"
          >
            <div className="relative w-28 h-28 rounded-2xl overflow-hidden shadow-xl bg-gradient-to-tr from-maroon-300/20 to-transparent">
              <img
                src="/images/image.png"
                alt="Sakshi in a candid moment"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/15 to-transparent" />
            </div>
          </motion.div>
          <div className="flex flex-wrap gap-2 mt-4">
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${darkMode ? 'bg-maroon-200 bg-opacity-50 text-white' : 'bg-maroon-200 text-white'}`}>Developer</span>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${darkMode ? 'bg-maroon-200 bg-opacity-50 text-white' : 'bg-maroon-200 text-white'}`}>Musician</span>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${darkMode ? 'bg-maroon-200 bg-opacity-50 text-white' : 'bg-maroon-200 text-white'}`}>Singer</span>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${darkMode ? 'bg-maroon-200 bg-opacity-50 text-white' : 'bg-maroon-200 text-white'}`}>Artist</span>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${darkMode ? 'bg-maroon-200 bg-opacity-50 text-white' : 'bg-maroon-200 text-white'}`}>Writer</span>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${darkMode ? 'bg-maroon-200 bg-opacity-50 text-white' : 'bg-maroon-200 text-white'}`}>Astronomer</span>
          </div>
          
          {/* Fun Facts Section - Clickable Icons */}
          <div className="mt-6 pt-6 border-t border-white border-opacity-10">
            <h3 className={`text-base font-semibold mb-1 ${darkMode ? 'text-white' : 'text-gray-700'}`}>Fun Facts</h3>
            <p className={`text-xs mb-4 ${darkMode ? 'text-white' : 'text-gray-600'}`}>Click on an icon to learn more</p>
            <div className="flex flex-wrap justify-center gap-4">
              {[
                { 
                  icon: '☕', 
                  fact: 'I believe great ideas usually arrive after coffee, during walks, or while explaining something to someone else.',
                },
                { 
                  icon: '🌌', 
                  fact: 'I\'m deeply fascinated by astronomy, quantum physics, astrophysics and big unanswered questions — the universe humbles me more than any compiler error.',
                },
                { 
                  icon: '✍️', 
                  fact: 'I write poetry and stories, usually late at night, often about humans, systems, philosophies and the strange overlap between them.',
                },
                { 
                  icon: '🎤', 
                  fact: 'Outside tech, I\'m a singer and performer — I\'ve led college bands, competed, and performed live on stage.',
                },
                { 
                  icon: '🌱', 
                  fact: 'I care deeply about humanitarian causes, especially supporting women, the elderly, and animals.',
                },
                { 
                  icon: '💭', 
                  fact: 'I enjoy dark humor and jokes, and enjoy deep talks.',
                }
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: false, amount: 0 }}
                  transition={{ delay: idx * 0.1, duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                  whileHover={{ scale: 1.15 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    const isMobileView = window.innerWidth < 768;
                    
                    let x, y;
                    
                    if (isMobileView) {
                      // Center on mobile screen - always use viewport center
                      // This ensures it's always centered and visible
                      x = window.innerWidth / 2;
                      y = window.innerHeight / 2;
                    } else {
                      // Desktop: position relative to icon
                      const tooltipWidth = 320; // w-80 = 320px
                      const tooltipHeight = 120; // approximate height
                      const rect = e.currentTarget.getBoundingClientRect();
                      const padding = 10;
                      
                      // Calculate center x position
                      x = rect.left + rect.width / 2;
                      // Ensure tooltip doesn't go off left/right edges
                      x = Math.max(tooltipWidth / 2 + padding, Math.min(x, window.innerWidth - tooltipWidth / 2 - padding));
                      
                      // Position below icon with some spacing
                      y = rect.top + rect.height + 15;
                      // If tooltip would go off bottom, position above icon instead
                      if (y + tooltipHeight > window.innerHeight - padding) {
                        y = rect.top - tooltipHeight - 15;
                      }
                      // Ensure tooltip doesn't go off top edge
                      y = Math.max(padding, y);
                    }
                    
                    setTooltipPosition({ x, y, isMobile: isMobileView });
                    setSelectedFact(selectedFact === idx ? null : idx);
                  }}
                  ref={(el) => factIconRefs.current[idx] = el}
                  className="relative cursor-pointer fun-fact-icon"
                >
                  <motion.div
                    animate={{ 
                      scale: selectedFact === idx ? 1.2 : 1,
                      rotate: selectedFact === idx ? 360 : 0
                    }}
                    transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                    className={`w-12 h-12 rounded-full flex items-center justify-center shadow-lg hover:shadow-2xl ring-2 transition-all ${
                      darkMode
                        ? 'bg-gray-800/80 ring-white/20 border border-white/10'
                        : 'bg-white/90 ring-maroon-300/20 border border-maroon-300/20'
                    }`}
                  >
                    <span className="text-xl">{item.icon}</span>
                  </motion.div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 30 }}
          viewport={{ once: false, amount: 0 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          id="skills"
          className={`backdrop-blur p-6 rounded-xl border transition-all duration-150 ${
            darkMode 
              ? 'bg-white/5 hover:bg-white/10 border-white/10' 
              : 'bg-gray-50 border-gray-200'
          }`}
        >
          <h2 className="text-2xl font-semibold mb-4">Technical Skills</h2>
          
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-base mb-2 flex items-center gap-2">
                <FaCode className="text-maroon-200" />
                <span className={darkMode ? 'text-white' : 'text-maroon-200'}>Programming Languages</span>
              </h3>
              <div className="flex flex-wrap gap-2">
                {['Java', 'Python', 'C', 'C++', 'JavaScript', 'SQL'].map((lang) => (
                  <span
                    key={lang}
                    className={`px-3 py-1 rounded-lg text-xs font-medium ${
                      darkMode
                        ? 'bg-maroon-200 bg-opacity-60 text-white border border-maroon-200'
                        : 'bg-white text-charcoal border border-maroon-200'
                    }`}
                  >
                    {lang}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-base mb-2 flex items-center gap-2">
                <FaReact className="text-maroon-200" />
                <span className={darkMode ? 'text-white' : 'text-maroon-200'}>Frameworks</span>
              </h3>
              <div className="flex flex-wrap gap-2">
                {['React', 'Node.js', 'Spring Boot', 'Django', 'Flask', 'PyTorch', 'TensorFlow'].map((tech) => (
                  <span
                    key={tech}
                    className={`px-3 py-1 rounded-lg text-xs font-medium ${
                      darkMode
                        ? 'bg-maroon-200 bg-opacity-60 text-white border border-maroon-200'
                        : 'bg-white text-charcoal border border-maroon-200'
                    }`}
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-base mb-2 flex items-center gap-2">
                <FaCloud className="text-maroon-200" />
                <span className={darkMode ? 'text-white' : 'text-maroon-200'}>Cloud & DevOps</span>
              </h3>
              <div className="flex flex-wrap gap-2">
                {['AWS', 'Docker', 'Kubernetes', 'Jenkins', 'GCP', 'Redis', 'Kafka'].map((tool) => (
                  <span
                    key={tool}
                    className={`px-3 py-1 rounded-lg text-xs font-medium ${
                      darkMode
                        ? 'bg-maroon-200 bg-opacity-60 text-white border border-maroon-200'
                        : 'bg-white text-charcoal border border-maroon-200'
                    }`}
                  >
                    {tool}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-base mb-2 flex items-center gap-2">
                <FaDatabase className="text-maroon-200" />
                <span className={darkMode ? 'text-white' : 'text-maroon-200'}>Databases</span>
              </h3>
              <div className="flex flex-wrap gap-2">
                {['DynamoDB', 'MongoDB', 'PostgreSQL', 'MySQL', 'NoSQL'].map((db) => (
                  <span
                    key={db}
                    className={`px-3 py-1 rounded-lg text-xs font-medium ${
                      darkMode
                        ? 'bg-maroon-200 bg-opacity-60 text-white border border-maroon-200'
                        : 'bg-white text-charcoal border border-maroon-200'
                    }`}
                  >
                    {db}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-base mb-2 flex items-center gap-2">
                <FaBrain className="text-maroon-200" />
                <span className={darkMode ? 'text-white' : 'text-maroon-200'}>AI & ML</span>
              </h3>
              <div className="flex flex-wrap gap-2">
                {['Machine Learning', 'Deep Learning', 'LLMs', 'GPT-4', 'Gemini', 'Grok', 'BERT', 'GANs', 'RLHF'].map((ai) => (
                  <span
                    key={ai}
                    className={`px-3 py-1 rounded-lg text-xs font-medium ${
                      darkMode
                        ? 'bg-maroon-200 bg-opacity-60 text-white border border-maroon-200'
                        : 'bg-white text-charcoal border border-maroon-200'
                    }`}
                  >
                    {ai}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Work Experience & Education Section - Side by Side */}
      <section id="experience" className="m-6 md:m-10 z-10 relative scroll-mt-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Education Section - Left on desktop, Second on mobile - Timeline View */}
            <div className={`backdrop-blur p-6 md:p-8 rounded-xl order-2 md:order-1 ${
              darkMode ? 'bg-white bg-opacity-10' : 'bg-white bg-opacity-90'
            }`}>
              <motion.h2 
                initial={{ opacity: 0, y: -20 }}
                whileInView={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                viewport={{ once: false, amount: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="text-2xl md:text-3xl font-bold mb-6 text-center flex items-center justify-center gap-2"
              >
                <FaGraduationCap className="text-maroon-200" />
                Education
              </motion.h2>

              {/* Timeline View */}
              <div className="relative pl-8 md:pl-12">
                {/* Vertical Timeline Line */}
                <div className={`absolute left-4 md:left-6 top-0 bottom-0 w-0.5 ${darkMode ? 'bg-maroon-200/30' : 'bg-maroon-200/30'}`}></div>
                
                {education.map((edu, index) => {
                  const IconComponent = edu.icon || FaGraduationCap;
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      className="relative mb-6 last:mb-0"
                    >
                      {/* Timeline Dot */}
                      <div className={`absolute left-0 top-0 transform -translate-x-1/2 w-3 h-3 rounded-full ${darkMode ? 'bg-maroon-200' : 'bg-maroon-200'} border-2 ${darkMode ? 'border-gray-900' : 'border-white'} z-10`}></div>
                      
                      {/* Year Badge */}
                      <div className={`absolute left-0 top-0 transform -translate-x-full -translate-y-1/2 px-3 py-1 rounded ${darkMode ? 'bg-maroon-200 text-white' : 'bg-maroon-200 text-white'} text-xs font-bold whitespace-nowrap shadow-lg`}>
                        {edu.year || (edu.period.includes('Present') ? 'Present' : edu.period.split(' ').slice(-1)[0])}
                      </div>

                      {/* Content Card */}
                      <div className={`ml-4 ${darkMode ? 'bg-gray-800/80' : 'bg-white/90'} backdrop-blur p-4 rounded-lg shadow-lg border ${
                        darkMode ? 'border-gray-700' : 'border-gray-200'
                      }`}>
                        <div className="flex items-start gap-3 mb-2">
                          <IconComponent className={`text-xl ${edu.iconColor || 'text-maroon-200'} flex-shrink-0 mt-1`} />
                          <div className="flex-1">
                            <h3 className={`text-base font-bold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              {edu.degree}
                            </h3>
                            <p className={`text-sm font-semibold mb-1 ${darkMode ? 'text-white' : 'text-maroon-200'}`}>
                              {edu.field}
                            </p>
                            <p className={`text-xs mb-2 ${darkMode ? 'text-white' : 'text-gray-600'}`}>
                              {edu.university}
                            </p>
                          </div>
                        </div>
                        
                        <div className={`space-y-1.5 text-xs ${darkMode ? 'text-white' : 'text-gray-600'}`}>
                          <div className="flex items-center gap-1.5">
                            <FaCalendarAlt className="text-maroon-200" />
                            <span>{edu.period}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <FaMapMarkerAlt className="text-maroon-200" />
                            <span>{edu.location}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <FaTrophy className="text-yellow-400" />
                            <span className={`font-semibold ${darkMode ? 'text-white' : 'text-maroon-200'}`}>GPA: {edu.gpa}</span>
                          </div>
                        </div>
                        
                        {(edu.focus || edu.coursework) && (
                          <p className={`text-xs mt-2 leading-relaxed ${darkMode ? 'text-white' : 'text-gray-700'}`}>
                            {edu.focus || edu.coursework}
                          </p>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* Work Experience Section - Right on desktop, First on mobile - Timeline View */}
            <div className={`backdrop-blur p-6 md:p-8 rounded-xl order-1 md:order-2 ${
              darkMode ? 'bg-white bg-opacity-10' : 'bg-white bg-opacity-90'
            }`}>
              <motion.h2 
                initial={{ opacity: 0, y: -20 }}
                whileInView={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                viewport={{ once: false, amount: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="text-2xl md:text-3xl font-bold mb-6 text-center flex items-center justify-center gap-2"
              >
                <FaBriefcase className="text-maroon-200" />
                Work Experience
              </motion.h2>
              
              {/* Timeline View */}
              <div className="relative pl-8 md:pl-12">
                {/* Vertical Timeline Line */}
                <div className={`absolute left-4 md:left-6 top-0 bottom-0 w-0.5 ${darkMode ? 'bg-maroon-200/30' : 'bg-maroon-200/30'}`}></div>
                
                {workExperience.map((job, index) => {
                  const isExpanded = expandedAchievements[index] === true; // Default to collapsed
                  const endYear = job.period.split(' ').slice(-1)[0];
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      className="relative mb-6 last:mb-0"
                    >
                      {/* Timeline Dot */}
                      <div className={`absolute left-0 top-0 transform -translate-x-1/2 w-3 h-3 rounded-full ${darkMode ? 'bg-maroon-200' : 'bg-maroon-200'} border-2 ${darkMode ? 'border-gray-900' : 'border-white'} z-10`}></div>
                      
                      {/* Year Badge */}
                      <div className={`absolute left-0 top-0 transform -translate-x-full -translate-y-1/2 px-3 py-1 rounded ${darkMode ? 'bg-maroon-200 text-white' : 'bg-maroon-200 text-white'} text-xs font-bold whitespace-nowrap shadow-lg`}>
                        {endYear}
                      </div>

                      {/* Content Card */}
                      <div className={`ml-4 ${darkMode ? 'bg-gray-800/80' : 'bg-white/90'} backdrop-blur p-4 rounded-lg shadow-lg border ${
                        darkMode ? 'border-gray-700' : 'border-gray-200'
                      }`}>
                        {/* Header */}
                        <div className="flex items-start gap-3 mb-3">
                          <FaBriefcase className={`text-xl ${darkMode ? 'text-white' : 'text-maroon-200'} flex-shrink-0 mt-1`} />
                          <div className="flex-1">
                            <h3 className={`text-lg font-bold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              {job.title}
                            </h3>
                            <p className={`text-base font-semibold mb-2 ${darkMode ? 'text-white' : 'text-maroon-200'}`}>
                              {job.company}
                            </p>
                            <div className={`flex flex-wrap items-center gap-3 text-xs ${darkMode ? 'text-white' : 'text-gray-600'}`}>
                              <div className="flex items-center">
                                <FaMapMarkerAlt className="mr-1" />
                                <span>{job.location}</span>
                              </div>
                              <div className="flex items-center">
                                <FaCalendarAlt className="mr-1" />
                                <span>{job.period}</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Detailed Description - Full on desktop, truncated on mobile with View More */}
                        <div className="mb-4">
                          {/* Desktop: Always show full description */}
                          <p className={`hidden md:block text-xs md:text-sm leading-relaxed ${darkMode ? 'text-white' : 'text-gray-700'}`}>
                            {job.detailedDescription}
                          </p>
                          {/* Mobile: Show brief description, with option to expand */}
                          <div className="md:hidden">
                            <p className={`text-xs leading-relaxed ${darkMode ? 'text-white' : 'text-gray-700'}`}>
                              {expandedDescriptions[index] ? job.detailedDescription : job.briefDescription}
                            </p>
                            {!expandedDescriptions[index] && (
                              <button
                                onClick={() => setExpandedDescriptions(prev => ({ ...prev, [index]: true }))}
                                className={`mt-2 text-xs font-semibold ${darkMode ? 'text-maroon-200 hover:text-maroon-300' : 'text-maroon-200 hover:text-maroon-300'} transition-colors flex items-center gap-1`}
                              >
                                View More
                                <FaChevronDown className="text-xs" />
                              </button>
                            )}
                            {expandedDescriptions[index] && (
                              <button
                                onClick={() => setExpandedDescriptions(prev => ({ ...prev, [index]: false }))}
                                className={`mt-2 text-xs font-semibold ${darkMode ? 'text-maroon-200 hover:text-maroon-300' : 'text-maroon-200 hover:text-maroon-300'} transition-colors flex items-center gap-1`}
                              >
                                View Less
                                <FaChevronUp className="text-xs" />
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Key Achievements & Responsibilities - Collapsible with Indicator */}
                        <div className="mb-4">
                          <button
                            onClick={() => setExpandedAchievements(prev => ({ ...prev, [index]: !isExpanded }))}
                            className={`w-full flex items-center justify-between mb-3 p-2 rounded-lg transition-all ${
                              darkMode 
                                ? 'hover:bg-white/5 text-white' 
                                : 'hover:bg-gray-50 text-gray-800'
                            } group`}
                          >
                            <div className="flex items-center gap-2">
                              <h4 className={`text-sm md:text-base font-semibold`}>
                                Key Achievements & Responsibilities:
                              </h4>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                darkMode 
                                  ? 'bg-maroon-200/20 text-maroon-200 border border-maroon-200/30' 
                                  : 'bg-maroon-200/10 text-maroon-200 border border-maroon-200/30'
                              } group-hover:scale-105 transition-transform`}>
                                Click to {isExpanded ? 'collapse' : 'expand'}
                              </span>
                            </div>
                            {isExpanded ? (
                              <FaChevronUp className={`text-maroon-200 transition-transform group-hover:scale-110`} />
                            ) : (
                              <FaChevronDown className={`text-maroon-200 transition-transform group-hover:scale-110`} />
                            )}
                          </button>
                          {isExpanded && (
                            <motion.ul
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.3 }}
                              className="space-y-2 overflow-hidden"
                            >
                              {job.achievements.map((achievement, idx) => (
                                <li key={idx} className="flex items-start text-xs md:text-sm">
                                  <span className={`mr-2 ${darkMode ? 'text-white' : 'text-maroon-200'} mt-1 flex-shrink-0`}>▸</span>
                                  <span className={darkMode ? 'text-white' : 'text-gray-700'}>{achievement}</span>
                                </li>
                              ))}
                            </motion.ul>
                          )}
                        </div>

                        {/* Technologies & Tools - Always Visible */}
                        <div>
                          <h4 className={`text-sm md:text-base font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                            Technologies & Tools:
                          </h4>
                          <div className="flex flex-wrap gap-1.5">
                            {job.technologies.map((tech, idx) => (
                              <span key={idx} className={`px-2 py-1 rounded-full text-xs font-medium ${darkMode ? 'bg-maroon-200 bg-opacity-50 text-white' : 'bg-maroon-200 text-white'}`}>{tech}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      <motion.section 
        ref={projectsRef}
        id="projects" 
        style={{ y: projectsY }}
        className="m-6 md:m-10 z-10 relative scroll-mt-24"
      >
        <div
          className={`backdrop-blur p-4 md:p-6 rounded-xl ${
            darkMode ? 'bg-white/5' : 'bg-gray-50'
          }`}
        >
          <h2
            className={`text-2xl md:text-3xl font-bold mb-4 md:mb-6 text-center ${
              darkMode ? 'text-white' : 'text-gray-900'
            }`}
          >
            Featured Projects
          </h2>
          
          {/* Carousel Container */}
          <div className="relative">
            {/* Navigation Buttons - Mobile: one card, Desktop: 3 cards */}
            <button
              onClick={() => setCurrentProjectIndex((prev) => Math.max(0, prev - 1))}
              disabled={currentProjectIndex === 0}
              className={`absolute left-0 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full ${
                darkMode ? 'bg-white/10 hover:bg-white/20' : 'bg-white/80 hover:bg-white'
              } shadow-lg transition-all duration-200 -translate-x-4 md:-translate-x-8 disabled:opacity-50 disabled:cursor-not-allowed`}
              aria-label="Previous project"
            >
              <FaChevronLeft className={`text-xl ${darkMode ? 'text-white' : 'text-gray-900'}`} />
            </button>
            <button
              onClick={() => {
                // On mobile: move by 1, on desktop: move by 1 but max is projects.length - 3
                const maxIndex = isMobile ? projects.length - 1 : projects.length - 3;
                setCurrentProjectIndex((prev) => Math.min(maxIndex, prev + 1));
              }}
              disabled={isMobile ? currentProjectIndex >= projects.length - 1 : currentProjectIndex >= projects.length - 3}
              className={`absolute right-0 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full ${
                darkMode ? 'bg-white/10 hover:bg-white/20' : 'bg-white/80 hover:bg-white'
              } shadow-lg transition-all duration-200 translate-x-4 md:translate-x-8 disabled:opacity-50 disabled:cursor-not-allowed`}
              aria-label="Next project"
            >
              <FaChevronRight className={`text-xl ${darkMode ? 'text-white' : 'text-gray-900'}`} />
            </button>

            {/* Carousel - Shows 1 project on mobile, 3 on desktop */}
            <div className="overflow-hidden">
            <motion.div
                className="flex md:items-stretch"
                animate={{ 
                  x: `-${currentProjectIndex * (isMobile ? 100 : 100 / 3)}%` 
                }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
              >
                {projects.map((project, idx) => {
                  const ProjectIcon = project.icon;
                  const techColorClass = project.techColor === 'maroon-500' 
                    ? (darkMode ? 'bg-maroon-200/80 text-white border border-maroon-200/70' : 'bg-white text-maroon-200 border border-maroon-200/60')
                    : (darkMode ? 'bg-maroon-200/80 text-white border border-maroon-200/70' : 'bg-white text-maroon-200 border border-maroon-200/60');
                  
                  return (
                    <div key={project.id} className="w-full md:w-1/3 flex-shrink-0 px-2 md:px-4 md:flex">
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
                        className={`relative p-3 md:p-6 rounded-2xl md:rounded-3xl overflow-hidden shadow-xl md:shadow-2xl bg-gradient-to-b from-white/25 via-white/10 to-white/5 border border-white/15 backdrop-blur-xl cursor-default flex flex-col md:h-full md:w-full ${
                          darkMode ? 'bg-white/10 ring-2 md:ring-4 ring-maroon-300/15' : 'bg-white'
                        }`}
                      >
                        <div className="absolute -top-10 -left-8 h-28 w-28 rounded-full bg-maroon-300/25 blur-2xl" aria-hidden />
                        <div className="absolute -bottom-12 right-0 h-32 w-32 rounded-full bg-maroon-200/20 blur-3xl" aria-hidden />
                        <div className="flex-1 flex flex-col md:justify-between">
                          <div>
                            {project.award && (
                              <div className="flex items-center gap-1.5 mb-2 md:mb-3">
                    <FaTrophy className="text-yellow-400 text-xs md:text-sm" />
                                <span className="text-xs font-semibold text-yellow-400 leading-tight">{project.award}</span>
                  </div>
                            )}
                            <ProjectIcon 
                              className="text-2xl md:text-4xl mb-2 md:mb-3" 
                              style={project.iconStyle || {}}
                            />
                            <h3 className={`text-lg md:text-2xl font-bold mb-2 md:mb-3 ${darkMode ? 'text-white' : ''}`}>{project.name}</h3>
                            <p className={`text-xs md:text-base mb-3 md:mb-4 leading-relaxed ${darkMode ? 'text-white' : 'text-gray-700'}`}>
                              {project.description}
                            </p>
                            <div className="flex flex-wrap gap-1 md:gap-1.5 mb-3 md:mb-4">
                              {project.technologies.map(tech => (
                                <span
                                  key={tech}
                                  className={`px-2 py-0.5 md:px-2.5 md:py-1 text-xs rounded font-semibold ${techColorClass}`}
                                >
                                  {tech}
                                </span>
                  ))}
                  </div>
                          </div>
                          <div className="flex flex-wrap gap-1.5 md:gap-2 mt-2 md:mt-4 justify-center">
                            {project.links.map((link) => (
                  <a
                                key={link.label}
                                href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                                className="text-center px-2.5 py-1.5 md:px-3 md:py-2 bg-maroon-200 rounded-lg hover:opacity-90 text-white font-medium transition-all duration-150 text-xs md:text-sm"
                              >
                                {link.label}
                              </a>
                            ))}
                          </div>
                        </div>
            </motion.div>
                </div>
                  );
                })}
            </motion.div>
            </div>

            {/* Carousel Indicators */}
            <div className="flex justify-center gap-2 mt-4 md:mt-6">
              {projects.map((_, idx) => {
                // On desktop, only allow clicking indicators that allow showing 3 cards
                const maxIndex = isMobile ? projects.length - 1 : projects.length - 3;
                const isClickable = idx <= maxIndex;
                return (
                  <button
                    key={idx}
                    onClick={() => {
                      if (isClickable) {
                        setCurrentProjectIndex(idx);
                      }
                    }}
                    disabled={!isClickable}
                    className={`h-2 rounded-full transition-all duration-200 ${
                      idx === currentProjectIndex
                        ? 'w-8 bg-maroon-200'
                        : isClickable 
                          ? 'w-2 bg-gray-400 hover:bg-gray-500'
                          : 'w-2 bg-gray-300 opacity-50 cursor-not-allowed'
                    }`}
                    aria-label={`Go to project ${idx + 1}`}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </motion.section>

      <section id="contact" className="mt-20 md:mt-32 mb-6 md:mb-10 z-10 relative scroll-mt-24">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          viewport={{ once: false, amount: 0 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className={`relative overflow-hidden backdrop-blur-xl p-6 md:p-7 rounded-3xl max-w-lg mx-auto shadow-2xl ${
            darkMode ? 'bg-white/10 ring-4 ring-maroon-300/15' : 'bg-white'
          }`}
        >
          <div className="absolute -top-10 -left-10 h-28 w-28 rounded-full bg-maroon-300/20 blur-3xl" aria-hidden />
          <div className="absolute bottom-0 right-0 h-32 w-32 rounded-full bg-maroon-200/20 blur-3xl" aria-hidden />
          <h2 className="text-xl md:text-2xl font-semibold mb-4 text-center">Get In Touch</h2>
          
          {/* Contact Form */}
          <form ref={contactForm} onSubmit={handleContactSubmit} className="space-y-3">
            <div>
              <label htmlFor="user_name" className={`block text-xs font-medium mb-1 ${darkMode ? 'text-white' : 'text-charcoal'}`}>
                Name *
              </label>
              <input
                type="text"
                id="user_name"
                name="user_name"
                required
                minLength={2}
                className={`w-full px-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-maroon-200 transition-all ${
                  darkMode ? 'bg-gray-700 text-white border border-gray-600 hover:border-gray-500' : 'bg-gray-50 text-charcoal border border-gray-200 hover:border-maroon-200'
                }`}
                placeholder="Your Name"
              />
            </div>

            <div>
              <label htmlFor="user_email" className={`block text-xs font-medium mb-1 ${darkMode ? 'text-white' : 'text-charcoal'}`}>
                Email *
              </label>
              <input
                type="email"
                id="user_email"
                name="user_email"
                required
                className={`w-full px-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-maroon-200 transition-all ${
                  darkMode ? 'bg-gray-700 text-white border border-gray-600 hover:border-gray-500' : 'bg-gray-50 text-charcoal border border-gray-200 hover:border-maroon-200'
                }`}
                placeholder="your.email@example.com"
              />
            </div>

            <div>
              <label htmlFor="message" className={`block text-xs font-medium mb-1 ${darkMode ? 'text-white' : 'text-charcoal'}`}>
                Message *
              </label>
              <textarea
                id="message"
                name="message"
                required
                rows={3}
                minLength={10}
                className={`w-full px-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-maroon-200 resize-none transition-all ${
                  darkMode ? 'bg-gray-700 text-white border border-gray-600 hover:border-gray-500' : 'bg-gray-50 text-charcoal border border-gray-200 hover:border-maroon-200'
                }`}
                placeholder="Your message or contact details..."
              />
            </div>

            {formStatus.message && (
              <div className={`p-2 text-xs rounded-lg ${
                formStatus.type === 'success' 
                  ? 'bg-maroon-200 bg-opacity-20 text-deep-charcoal border border-maroon-200' 
                  : 'bg-maroon-500 bg-opacity-20 text-maroon-200 border border-maroon-500'
              }`}>
                {formStatus.message}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full py-2 px-4 rounded-lg text-sm font-semibold transition-colors ${
                isSubmitting
                  ? 'bg-gray-500 text-gray-300 cursor-not-allowed'
                  : 'bg-maroon-200 text-white hover:opacity-90'
              }`}
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
          </form>

          {/* Social Media Links */}
          <div className="mt-4 pt-4 border-t border-white border-opacity-20">
            <p className={`text-center mb-3 text-xs ${darkMode ? 'text-white' : 'text-gray-700'}`}>
              Or connect with me on social media
            </p>
            <div className="flex justify-center space-x-4">
              <a href="https://github.com/stripathy1999" target="_blank" rel="noopener noreferrer" className="hover:scale-110 transition-transform">
                <FaGithub size={22} />
              </a>
              <a href="https://www.linkedin.com/in/sakshi-sanskruti-tripathy/" target="_blank" rel="noopener noreferrer" className="hover:scale-110 transition-transform">
                <FaLinkedin size={22} />
              </a>
              <a href="https://www.instagram.com/_sakshi_tripathy_" target="_blank" rel="noopener noreferrer" className="hover:scale-110 transition-transform">
                <FaInstagram size={22} />
              </a>
              <a href="https://www.facebook.com/sakshi.tripathy.5" target="_blank" rel="noopener noreferrer" className="hover:scale-110 transition-transform">
                <FaFacebook size={22} />
              </a>
              <a href="https://www.youtube.com/@thebystandervlogs2668" target="_blank" rel="noopener noreferrer" className="hover:scale-110 transition-transform">
                <FaYoutube size={22} />
              </a>
            </div>
          </div>
        </motion.div>
      </section>

      <footer className="text-center mb-10 z-10 relative">
        <p className={`text-sm md:text-base mb-2 italic ${darkMode ? 'text-white' : 'text-gray-700'}`}>
          I'm really excited to work, collaborate, and contribute to innovative projects. Feel free to reach out—I'm very excited to connect!
        </p>
        <p className={`text-xs ${darkMode ? 'text-white' : 'text-gray-600'}`}>
          © 2025 Sakshi Tripathy
        </p>
      </footer>
    </div>
  );
}

